import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API_URL } from "@/lib/api";
import ReviewReply from "@/components/ReviewReply";
import { usePropertyFilter } from "@/context/PropertyFilter";
import { useAuth } from "@/App";
import OnboardingBanner from "@/components/OnboardingBanner";
import { useBookings, useProperties, useReviews, useInvalidate } from "@/lib/queries";
import { formatINR, formatDate, PLATFORMS, canEdit } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Star, User, PencilSimple, NavigationArrow, Spinner, WarningCircle, LockSimple, Prohibit, IdentificationCard, Phone, WhatsappLogo, ClockCounterClockwise, ShareNetwork, CheckCircle, Trash, Confetti, CalendarPlus, Copy, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";

const OTA_LABELS = { airbnb: "Airbnb", "booking.com": "Booking.com", makemytrip: "MakeMyTrip", goibibo: "Goibibo", vrbo: "VRBO", agoda: "Agoda" };
const OTA_COLORS = {
  airbnb: "bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800/40",
  "booking.com": "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/40",
  makemytrip: "bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800/40",
  goibibo: "bg-purple-50 text-purple-600 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800/40",
  vrbo: "bg-teal-50 text-teal-600 border-teal-200 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-800/40",
  agoda: "bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800/40",
};

export default function BookingCalendar() {
  const { selectedPropertyId, selectedRoomId } = usePropertyFilter();
  const bookingFilters = {
    ...(selectedPropertyId ? { property_id: selectedPropertyId } : {}),
    ...(selectedRoomId     ? { room_id: selectedRoomId }         : {}),
  };
  const { data: rawBookings, isLoading: loading } = useBookings(bookingFilters);
  const bookings = rawBookings?.items ?? rawBookings ?? [];
  const { data: properties = [] } = useProperties();
  const { data: reviews = [] } = useReviews();
  const invalidate = useInvalidate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const { user } = useAuth();
  // The host grants this as "Bookings & Calendar". Without it the server refuses every
  // approve/decline/accept, so showing the buttons only produced a 403 toast.
  const mayDecideBookings = canEdit(user, "calendar");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [form, setForm] = useState(defaultBookingForm());
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState({ property_id: "", guest_name: "", rating: 5, review_text: "", date: new Date().toISOString().slice(0, 10) });
  const [detectingState, setDetectingState] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockForm, setBlockForm] = useState({ property_id: "", check_in: "", check_out: "", label: "Maintenance" });
  const [savingBlock, setSavingBlock] = useState(false);
  const [blockMode, setBlockMode] = useState("block");        // "block" | "unblock"
  const [bookedOverlap, setBookedOverlap] = useState(null);   // guests holding dates being reopened
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [nudgingId, setNudgingId] = useState(null);
  const [sendingReviewId, setSendingReviewId] = useState(null);
  const [sendingWaId, setSendingWaId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [declineConfirmId, setDeclineConfirmId] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [reportReason, setReportReason] = useState("");
  // Stay-extension offer (#6)
  const [extendBooking, setExtendBooking] = useState(null); // booking being extended
  const [extPct, setExtPct]       = useState(15);
  const [extBusy, setExtBusy]     = useState(false);
  const [extResult, setExtResult] = useState(null);   // offer response {offer_url, wa_text, ...}
  const [extNote, setExtNote]     = useState("");      // optional host note (sent compliantly)
  const [extSending, setExtSending] = useState(false);
  const [extSent, setExtSent]     = useState(false);
  // Discount a specific day (calendar)
  const [discDayOpen, setDiscDayOpen] = useState(false);
  const [discDayProp, setDiscDayProp] = useState("");
  const [discDayDate, setDiscDayDate] = useState("");
  const [discDayEnd, setDiscDayEnd] = useState("");  // optional: same price through this date
  const [discDayPct, setDiscDayPct]   = useState(15);
  const [discDayExact, setDiscDayExact] = useState("");   // exact rupee price for the date, beats the slider
  const [discDayBusy, setDiscDayBusy] = useState(false);

  // Deep link from notifications (?booking=<id>): jump to the booking's week,
  // scroll it into view and highlight it so the host instantly sees which
  // guest/booking the "ID uploaded" message was about.
  // Keep the month grid following the selected date (e.g. jumping to a booking
  // in another month), while the grid's own ‹ › nav can move independently.
  useEffect(() => {
    if (selectedDate) setCalMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
  }, [selectedDate]);

  // Per-date notes (payment records etc). Airbnb hides past-date notes from the host,
  // so these are imported and then owned here.
  const [dayNotes, setDayNotes] = useState({});   // {"<propId>|<YYYY-MM-DD>": "note"}
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/calendar-notes");
        const map = {};
        (r.data || []).forEach(n => { map[`${n.property_id}|${n.date}`] = n.note; });
        setDayNotes(map);
      } catch { /* notes are optional, never block the calendar */ }
    })();
  }, []);

  // Notes for one date, across the properties currently in view. The month grid is shown
  // for "All properties" too, where there is no single rateContextProp to key on.
  const notesForDate = (ds) => {
    const pid = rateContextProp?.id || rateContextProp?.property_id;
    if (pid) {
      const n = dayNotes[`${pid}|${ds}`];
      return n ? [{ property: rateContextProp?.name || "", note: n }] : [];
    }
    return Object.entries(dayNotes)
      .filter(([k]) => k.endsWith(`|${ds}`))
      .map(([k, note]) => ({
        property: (properties.find(p => (p.id || p.property_id) === k.split("|")[0]) || {}).name || "",
        note,
      }));
  };

  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("booking") || "";
  useEffect(() => {
    if (!highlightId || !bookings.length) return;
    const b = bookings.find(x => (x.id || x.booking_id) === highlightId);
    if (!b?.check_in) return;
    try { setSelectedDate(new Date(`${b.check_in}T00:00:00`)); } catch { /* keep current week */ }
    const t = setTimeout(() =>
      document.getElementById(`bk-${highlightId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, bookings.length]);

  const fetchLeads = async () => {
    setLeadsLoading(true);
    try { const r = await api.get("/leads"); setLeads(r.data || []); }
    catch { /* silently fail */ }
    finally { setLeadsLoading(false); }
  };

  const nudgeLead = async (bookingId) => {
    setNudgingId(bookingId);
    try {
      await api.post(`/leads/${bookingId}/nudge`);
      toast.success("WhatsApp reminder sent to guest");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send reminder");
    } finally { setNudgingId(null); }
  };

  const sendReviewRequest = async (bookingId) => {
    setSendingReviewId(bookingId);
    try {
      const r = await api.post(`/bookings/${bookingId}/send-review-request`);
      const link = r.data?.link || `${window.location.origin}/review/${bookingId}`;
      if (r.data?.whatsapp_sent) {
        toast.success("Review request sent via WhatsApp!");
      } else {
        await navigator.clipboard.writeText(link);
        toast.success("Review link copied! Share it with your guest.", { duration: 4000 });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.info("Guest has already submitted a review.");
      } else {
        toast.error(detail || "Failed to send review request");
      }
    } finally { setSendingReviewId(null); }
  };

  const INDIAN_STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Andaman and Nicobar Islands","Chandigarh","Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry","Dadra and Nagar Haveli and Daman and Diu"];

  const detectState = () => {
    if (!navigator.geolocation) return;
    setDetectingState(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${coords.latitude}&lon=${coords.longitude}&format=json`, { headers: { "Accept-Language": "en" } });
          const data = await res.json();
          const raw = data.address?.state || "";
          const matched = INDIAN_STATES.find(s => s.toLowerCase() === raw.toLowerCase()) || raw;
          if (matched) {
            setForm(f => {
              const prop = properties.find(p => p.id === f.property_id);
              const propState = prop?.state || "";
              const supply_type = propState
                ? (matched.toLowerCase() === propState.toLowerCase() ? "intrastate" : "interstate")
                : f.supply_type;
              return { ...f, guest_state: matched, supply_type };
            });
          }
        } catch { /* silent */ }
        setDetectingState(false);
      },
      () => setDetectingState(false),
      { timeout: 8000 }
    );
  };

  const propMap = {};
  const propRateMap = {};
  const propBufferMap = {};
  const propMinGapMap = {};
  const propTimesMap = {};
  const propById = {};
  // Effective buffer mirrors the backend: an explicit buffer_days setting, or a
  // forced 1-day gap when the standard checkout→check-in window can't satisfy
  // the host's minimum turnover hours.
  // checkIn (YYYY-MM-DD): when given, respects buffer_effective_from so existing
  // back-to-back bookings are not retroactively shown as violating the new policy.
  const toMins = (t, dflt) => { const [h, m] = String(t || dflt).split(":"); return (parseInt(h) || 0) * 60 + (parseInt(m) || 0); };
  const effectiveBuffer = (p, checkIn = null) => {
    const effectiveFrom = p.buffer_effective_from;
    if (effectiveFrom && checkIn && checkIn < effectiveFrom) return p.buffer_days_prev || 0;
    const buf = p.buffer_days || 0;
    const minH = p.min_turnover_hours || 0;
    if (buf === 0 && minH > 0 && (toMins(p.checkin_time, "14:00") - toMins(p.checkout_time, "11:00")) < minH * 60) return 1;
    return buf;
  };
  properties.forEach(p => {
    propById[p.id] = p;
    propMap[p.id] = p.name;
    propRateMap[p.id] = p.nightly_rate || 0;
    propBufferMap[p.id] = effectiveBuffer(p);
    propMinGapMap[p.id] = p.min_turnover_hours || 0;
    propTimesMap[p.id] = { checkin: p.checkin_time || "14:00", checkout: p.checkout_time || "11:00" };
  });

  // Airbnb-style per-day nightly rate for the calendar day cells.
  // Uses the property's (or selected room's) rate_calendar entry for that date,
  // falling back to its base nightly_rate. Shown only when a single property/room
  // is in context (a specific filter is active, or the account has one property) —
  // an aggregated "all properties" view has no single meaningful nightly rate.
  const rateContextProp = selectedPropertyId
    ? propById[selectedPropertyId]
    : (properties.length === 1 ? properties[0] : null);
  const rateForDay = (dayStr) => {
    const prop = rateContextProp;
    if (!prop) return null;
    let cal  = prop.rate_calendar || [];
    let base = prop.nightly_rate || 0;
    if (selectedRoomId && Array.isArray(prop.rooms)) {
      const room = prop.rooms.find(r => r.room_id === selectedRoomId);
      if (room) { cal = room.rate_calendar || []; base = room.nightly_rate || base; }
    }
    const hit  = cal.find(e => e.date === dayStr);
    const rate = (hit && hit.rate) ? hit.rate : base;
    return rate > 0 ? rate : null;
  };

  // Promo/override details for a day, so the calendar can flag a discounted night
  // and offer to remove it in place. Mirrors rateForDay's property/room resolution.
  const rateInfoForDay = (dayStr) => {
    const prop = rateContextProp;
    if (!prop) return null;
    let cal  = prop.rate_calendar || [];
    let base = prop.nightly_rate || 0;
    if (selectedRoomId && Array.isArray(prop.rooms)) {
      const room = prop.rooms.find(r => r.room_id === selectedRoomId);
      if (room) { cal = room.rate_calendar || []; base = room.nightly_rate || base; }
    }
    const hit = cal.find(e => e.date === dayStr && e.rate && e.type !== "booked");
    const rate = (hit && hit.rate) ? hit.rate : base;
    if (!(rate > 0)) return null;
    const discounted = !!hit && base > 0 && Number(hit.rate) < base;
    return {
      rate, base,
      discounted,
      special: !!hit && base > 0 && Number(hit.rate) !== base,
      pct: hit?.discount_pct || (discounted ? Math.round((1 - Number(hit.rate) / base) * 100) : 0),
    };
  };

  // Open the discount dialog already pointed at this property + day, so a rate can be
  // discounted or a promo removed straight from the calendar.
  const openDiscountForDay = (dayStr) => {
    const pid = rateContextProp?.id || rateContextProp?.property_id;
    if (!pid) return;
    const info = rateInfoForDay(dayStr);
    setDiscDayProp(pid);
    setDiscDayDate(dayStr);
    setDiscDayEnd("");
    setDiscDayPct(info?.discounted && info.pct ? info.pct : 15);
    setDiscDayExact(info?.special && !info.discounted ? String(info.rate) : "");
    setDiscDayOpen(true);
  };

  // The host's typed price wins. They set what they charge their guest, so it
  // overrides the property's configured rate for these dates and the breakdown
  // is read off it (backend `_calc_booking_financials` does the same).
  // Untyped, each night is priced at the property's own rate for that date (its rate
  // calendar: weekend uplifts, promos), falling back to the base nightly rate.
  const defaultNightRates = (propId, checkIn, checkOut) => {
    const p = propById[propId];
    const base = propRateMap[propId] || 0;
    const cal = {};
    (p?.rate_calendar || []).forEach(e => { if (e?.date && e.rate > 0 && e.type !== "booked") cal[e.date] = e.rate; });
    const out = [];
    for (let d = new Date(checkIn + "T00:00:00"); localDateStr(d) < checkOut && out.length < 400; d.setDate(d.getDate() + 1)) {
      out.push(cal[localDateStr(d)] || base);
    }
    return out;
  };

  const computeBreakdown = (propId, checkIn, checkOut, hostAmount, chargeGst = false) => {
    const typed = parseFloat(hostAmount);
    const hasTyped = Number.isFinite(typed) && typed > 0;
    if (!propId || !checkIn || !checkOut || checkOut <= checkIn) return null;
    try {
      const perNight = defaultNightRates(propId, checkIn, checkOut);
      const nights = Math.max(1, perNight.length);
      const auto = perNight.reduce((a, r) => a + (r || 0), 0);
      if (!hasTyped && !(auto > 0)) return null;
      const subtotal = hasTyped ? typed : auto;
      const rate = Math.round(subtotal / nights);
      const rateVaries = !hasTyped && new Set(perNight).size > 1;
      // No GST unless the host switches it on (only possible with a GSTIN): the price is final.
      const gstRate = !chargeGst ? 0 : rate > 7500 ? 18 : rate >= 1000 ? 5 : 0;
      const gstAmt = Math.round(subtotal * gstRate / 100);
      return { nights, rate, subtotal, gstRate, gstAmt, total: subtotal + gstAmt, hostSet: hasTyped,
               minRate: Math.min(...perNight), maxRate: Math.max(...perNight), rateVaries };
    } catch { return null; }
  };

  // Use local date string (YYYY-MM-DD) to avoid UTC timezone shift in India
  const localDateStr = (date) => {
    const d = (date instanceof Date) ? date : new Date(typeof date === "string" ? date + "T00:00:00" : date);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  };

  const conflictCount = bookings.filter(b => b.status === "conflict").length;
  const pendingCount  = bookings.filter(b => b.status === "pending_approval").length;

  // Only bookings that actually hold dates — matches what the guest booking
  // page and the reserve endpoint count as blocking. Expired/cancelled holds
  // must not paint the calendar.
  const holdsDate = (b) => {
    if (b.status === "expired" || b.status === "cancelled") return false;
    if (b.status === "pending_payment" && b.reservation_expires_at) {
      return new Date(b.reservation_expires_at) > new Date();
    }
    return true;
  };

  // Bookings scoped to the selected property (or all if none selected)
  const calBookings = (selectedPropertyId ? bookings.filter(b => b.property_id === selectedPropertyId) : bookings).filter(holdsDate);

  // Same-day turnovers: a guest checks out the same day another checks in.
  // Flag them when early/late requests collide, or when they violate the
  // property's turnover policy (typically OTA-imported back-to-backs).
  const GUEST_STAY_STATUSES = ["confirmed", "pending_approval", "pending_payment"];
  const guestStays = calBookings.filter(b => GUEST_STAY_STATUSES.includes(b.status));
  const staysByCheckin = new Map();
  guestStays.forEach(b => {
    const key = `${b.property_id}|${b.check_in}`;
    if (!staysByCheckin.has(key)) staysByCheckin.set(key, []);
    staysByCheckin.get(key).push(b);
  });
  const turnoverIssues = [];
  guestStays.forEach(a => {
    (staysByCheckin.get(`${a.property_id}|${a.check_out}`) || []).forEach(bNext => {
      if (a === bNext) return;
      const requests = [];
      if (a.late_checkout_requested) requests.push(`${a.guest_name || "Previous guest"} requested late checkout`);
      if (bNext.early_checkin_requested) requests.push(`${bNext.guest_name || "Next guest"} requested early check-in`);
      const buf = propById[a.property_id] ? effectiveBuffer(propById[a.property_id], a.check_in) : 0;
      const minGap = propMinGapMap[a.property_id] || 0;
      if (requests.length > 0) {
        turnoverIssues.push({
          date: a.check_out, property_id: a.property_id,
          out: a.guest_name, in: bNext.guest_name,
          severity: "conflict",
          reason: requests.join(" · "),
        });
      } else if (buf > 0) {
        turnoverIssues.push({
          date: a.check_out, property_id: a.property_id,
          out: a.guest_name, in: bNext.guest_name,
          severity: "gap",
          reason: minGap > 0
            ? `Standard times don't leave your ${minGap}h minimum turnover gap`
            : `Violates your ${buf}-day gap setting (likely imported from an OTA)`,
        });
      }
    });
  });

  const { bookedDates, conflictDates, hostBlockedDates, checkoutDates, bufferDates } = calBookings.reduce((acc, b) => {
    try {
      let d = new Date(b.check_in + "T00:00:00");
      const checkoutDate = new Date(b.check_out + "T00:00:00");
      const target = b.status === "conflict" ? acc.conflictDates
        : b.status === "blocked" && b.source === "host_block" ? acc.hostBlockedDates
        : acc.bookedDates;
      while (d < checkoutDate) {
        target.add(localDateStr(d));
        d = new Date(d.getTime() + 86400000);
      }
      // Buffer / cleaning gap days around the stay (matches what guests see as
      // blocked): after the checkout and before the check-in.
      const buf = propById[b.property_id] ? effectiveBuffer(propById[b.property_id], b.check_in) : 0;
      if (buf > 0 && b.status !== "conflict" && !(b.status === "blocked" && b.source === "host_block")) {
        let bufD = new Date(checkoutDate);
        for (let i = 0; i < buf; i++) {
          acc.bufferDates.add(localDateStr(bufD));
          bufD = new Date(bufD.getTime() + 86400000);
        }
        let preD = new Date(new Date(b.check_in + "T00:00:00").getTime() - 86400000);
        for (let i = 0; i < buf; i++) {
          acc.bufferDates.add(localDateStr(preD));
          preD = new Date(preD.getTime() - 86400000);
        }
      }
      // Track checkout date separately (only for confirmed/pending bookings)
      if (b.status !== "blocked" && b.status !== "conflict" && b.check_out) {
        acc.checkoutDates.add(b.check_out);
      }
    } catch (e) { /* skip */ }
    return acc;
  }, { bookedDates: new Set(), conflictDates: new Set(), hostBlockedDates: new Set(), checkoutDates: new Set(), bufferDates: new Set() });

  const BLOCK_LABELS = ["Maintenance", "Personal Use", "Renovation", "Deep Cleaning", "Family Stay", "Other"];

  // Reopen a range on every OTA. If a guest holds any of the dates, the server refuses
  // until the host confirms again in the second window (`confirm_booked`).
  const handleUnblockDates = async (confirmBooked = false) => {
    if (!blockForm.property_id || !blockForm.check_in || !blockForm.check_out) {
      toast.error("Please fill in all fields"); return;
    }
    setSavingBlock(true);
    try {
      await api.post("/bookings/unblock", { property_id: blockForm.property_id, check_in: blockForm.check_in,
        check_out: blockForm.check_out, confirm_booked: confirmBooked });
      toast.success("Dates reopened on all booking sites");
      setBookedOverlap(null);
      setBlockDialogOpen(false);
      setBlockForm({ property_id: "", check_in: "", check_out: "", label: "Maintenance" });
      invalidate.afterBookingMutation();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.bookings) setBookedOverlap(d.bookings);
      else toast.error((typeof d === "string" && d) || "Failed to unblock dates");
    }
    setSavingBlock(false);
  };

  const handleBlockDates = async () => {
    if (!blockForm.property_id || !blockForm.check_in || !blockForm.check_out) {
      toast.error("Please fill in all fields"); return;
    }
    setSavingBlock(true);
    try {
      await api.post("/bookings/block", blockForm);
      toast.success("Dates blocked");
      setBlockDialogOpen(false);
      setBlockForm({ property_id: "", check_in: "", check_out: "", label: "Maintenance" });
      invalidate.afterBookingMutation();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to block dates");
    }
    setSavingBlock(false);
  };

  // Weekly view helpers
  const getWeekStart = (date) => {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay()); // Sunday = 0
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(getWeekStart(selectedDate || new Date()));
    d.setDate(d.getDate() + i);
    return d;
  });
  const shiftWeek = (n) => setSelectedDate(d => { const nd = new Date(d); nd.setDate(nd.getDate() + n * 7); return nd; });

  const selectedStr = selectedDate ? localDateStr(selectedDate) : "";

  const openEditBooking = (b) => {
    setEditBooking(b);
    setForm({
      property_id: b.property_id || "",
      guest_name: b.guest_name || "",
      guest_phone: b.guest_phone || "",
      guest_state: b.guest_state || "",
      check_in: b.check_in || "",
      check_out: b.check_out || "",
      amount: b.amount || "",
      amount_paid: b.amount_paid || "",
      platform: b.platform || "Airbnb",
      status: b.status || "confirmed",
      supply_type: b.supply_type || "",
      charge_gst: !b.gst_exempt && (b.gst_amount || b.tax_amount) > 0,
      custom_price: true,
      guest_id_url: b.guest_id_url || "",
      guest_ids: Array.isArray(b.guest_ids) ? b.guest_ids.map(g => ({ name: g.name || "", url: g.url || "" })) : [],
      notes: b.notes || "",
      early_checkin_requested: !!b.early_checkin_requested,
      late_checkout_requested: !!b.late_checkout_requested,
    });
    setDialogOpen(true);
  };

  const nextDayStr = (ds) => { const d = new Date(ds + "T00:00:00"); d.setDate(d.getDate() + 1); return localDateStr(d); };

  const handleFormChange = (field, value) => {
    setForm(f => {
      const updated = { ...f, [field]: value };
      // A stay needs at least one night. iPhones show a date in an empty check-out box,
      // so an unset or same-day check-out silently hid the price; default it to the next day.
      if (field === 'check_in' && value && (!updated.check_out || updated.check_out <= value)) {
        updated.check_out = nextDayStr(value);
      }
      if (['property_id', 'check_in', 'check_out'].includes(field)) {
        // Default: the property's own rates for the dates. A custom price stays as typed.
        if (!updated.custom_price) {
          const bd = computeBreakdown(updated.property_id, updated.check_in, updated.check_out, "");
          updated.amount = bd ? bd.subtotal : "";
        }
      }
      return updated;
    });
  };

  // ── Per-guest government IDs ──────────────────────────────────────────────
  const [guestIdUploading, setGuestIdUploading] = useState(null);
  const addGuestId = () => setForm(f => ({ ...f, guest_ids: [...f.guest_ids, { name: "", url: "" }] }));
  const removeGuestId = (i) => setForm(f => ({ ...f, guest_ids: f.guest_ids.filter((_, n) => n !== i) }));
  const updateGuestId = (i, patch) =>
    setForm(f => ({ ...f, guest_ids: f.guest_ids.map((g, n) => (n === i ? { ...g, ...patch } : g)) }));

  const uploadMainGuestId = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setGuestIdUploading("main");
    try {
      const { data } = await api.post("/book/upload-id", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data?.url) throw new Error("no url");
      setForm(f => ({ ...f, guest_id_url: data.url }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload that ID");
    } finally {
      setGuestIdUploading(null);
    }
  };

  const uploadGuestId = async (i, file) => {
    if (!file) return;
    // /book/upload-id is the only route that produces a guest_ids/ URL, which is what
    // the backend validates against. Same endpoint the guest check-in page uses.
    const fd = new FormData();
    fd.append("file", file);
    setGuestIdUploading(i);
    try {
      const { data } = await api.post("/book/upload-id", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data?.url) throw new Error("no url");
      updateGuestId(i, { url: data.url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload that ID");
    } finally {
      setGuestIdUploading(null);
    }
  };

  const handleSaveBooking = async () => {
    try {
      const { custom_price, charge_gst, ...rest } = form;
      // Untyped price = exactly what the preview shows (each night at its own rate).
      const auto = !custom_price ? computeBreakdown(form.property_id, form.check_in, form.check_out, "") : null;
      const payload = {
        ...rest,
        no_gst: !charge_gst,
        amount: auto ? auto.subtotal : (parseFloat(form.amount) || 0),
        amount_paid: parseFloat(form.amount_paid) || 0,
        // an empty row the user added but never filled must not fail validation
        guest_ids: (form.guest_ids || []).filter(g => g.url && g.name.trim()),
      };
      if (editBooking) {
        await api.put(`/bookings/${editBooking.id}`, payload);
        toast.success("Booking updated");
      } else {
        await api.post("/bookings", payload);
        toast.success("Booking created");
      }
      setDialogOpen(false);
      invalidate.afterBookingMutation();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save booking";
      toast.error(msg);
    }
  };

  const handleDeleteBooking = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking deleted");
      invalidate.afterBookingMutation();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete booking"); }
    finally { setDeleteConfirmId(null); }
  };

  const handleResolveConflict = async (id) => {
    if (!window.confirm("Cancel this conflicting booking? The existing confirmed booking will remain.")) return;
    try {
      await api.put(`/bookings/${id}`, { status: "cancelled" });
      toast.success("Conflict resolved — overlapping booking cancelled");
      invalidate.afterBookingMutation();
    } catch { toast.error("Failed to resolve conflict"); }
  };

  const handleApproveBooking = async (b) => {
    const bkId = b.booking_id || b.id;
    try {
      await api.post(`/book/property/${b.property_id}/approve/${bkId}`);
      toast.success(`Booking approved for ${b.guest_name}`);
      invalidate.afterBookingMutation();
    } catch { toast.error("Failed to approve booking"); }
  };

  const handleDeclineBooking = async (b) => {
    const bkId = b.booking_id || b.id;
    try {
      await api.post(`/book/property/${b.property_id}/decline/${bkId}`);
      setDeclineConfirmId(null);
      toast.success("Booking declined");
      invalidate.afterBookingMutation();
    } catch { toast.error("Failed to decline booking"); }
  };

  const reviewOtaBooking = async (b, action) => {
    const bkId = b.id || b.booking_id;
    try {
      await api.post(`/bookings/${bkId}/review`, { action, reason: action === "reject" ? reportReason.trim() : "" });
      setReportId(null); setReportReason("");
      toast.success(action === "accept" ? `Accepted the booking for ${b.guest_name}` : "Reported. HostFolio will cancel it with the booking site and confirm to you.");
      invalidate.afterBookingMutation();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update the booking"); }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Delete this review? This cannot be undone.")) return;
    try {
      await api.delete(`/reviews/${reviewId}`);
      toast.success("Review deleted");
      invalidate.afterReviewMutation();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete review");
    }
  };

  const handleSaveReview = async () => {
    try {
      await api.post("/reviews", { ...reviewForm, rating: parseInt(reviewForm.rating) || 5 });
      toast.success("Review added");
      setReviewDialogOpen(false);
      invalidate.afterReviewMutation();
    } catch (e) { toast.error("Failed to save"); }
  };

  const sendWhatsAppConfirmation = async (bookingId) => {
    setSendingWaId(bookingId);
    try {
      await api.post(`/bookings/${bookingId}/send-whatsapp-confirmation`);
      toast.success("Booking confirmation sent via WhatsApp!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send WhatsApp message");
    } finally { setSendingWaId(null); }
  };

  const openExtend = (b) => {
    setExtendBooking(b);
    setExtPct(15);
    setExtResult(null);
    setExtNote("");
    setExtSent(false);
  };

  const createExtension = async () => {
    if (!extendBooking) return;
    setExtBusy(true);
    try {
      const bid = extendBooking.booking_id || extendBooking.id;
      const { data } = await api.post(`/bookings/${bid}/extend-offer`, {
        discount_pct: extPct,
      });
      setExtResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the extension offer");
    } finally { setExtBusy(false); }
  };

  const sendExtensionWa = async () => {
    if (!extResult?.offer_token) return;
    setExtSending(true);
    try {
      const { data } = await api.post(`/bookings/extend-offer/${extResult.offer_token}/send`, { note: extNote });
      if (data.sent) {
        setExtSent(true);
        toast.success(data.note_sent ? "Offer + your note sent to the guest on WhatsApp ✓" : "Offer sent to the guest on WhatsApp ✓");
      } else {
        toast("WhatsApp auto-send is being set up (template pending Meta approval). Use 'send from my WhatsApp' below for now.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send on WhatsApp");
    } finally { setExtSending(false); }
  };

  const openDiscountDay = () => {
    const firstPid = properties[0]?.id || properties[0]?.property_id || "";
    setDiscDayProp(selectedPropertyId || firstPid);
    setDiscDayDate(selectedStr || localDateStr(new Date()));
    setDiscDayEnd("");
    setDiscDayPct(15);
    setDiscDayOpen(true);
  };

  const applyDiscountDay = async () => {
    if (!discDayProp || !discDayDate) { toast.error("Pick a property and a date"); return; }
    setDiscDayBusy(true);
    try {
      const exact = Number(discDayExact) || 0;
      const { data } = await api.post("/pricing/discount", {
        property_id: discDayProp, date: discDayDate,
        ...(discDayEnd && discDayEnd > discDayDate ? { end_date: discDayEnd } : {}),
        ...(exact ? { rate: exact } : { discount_pct: discDayPct }),
      });
      if (!data.nights) {
        toast(data.message || "That day is already booked");
      } else {
        const when = discDayEnd && discDayEnd > discDayDate
          ? `${discDayDate} to ${discDayEnd} (${data.nights} open night${data.nights === 1 ? "" : "s"})` : discDayDate;
        toast.success(
          (exact ? `${when} set to ₹${data.rate?.toLocaleString("en-IN")}/night`
                 : `${data.discount_pct ?? discDayPct}% off applied for ${when} → ₹${data.rate?.toLocaleString("en-IN")}/night`) +
          (data.below_floor ? ` · below your ₹${data.price_floor?.toLocaleString("en-IN")} floor` : "") +
          (data.pushed ? " · pushed to OTAs ✓" : data.routed ? " · sent to the pricing engine, booking sites update once it is applied" : "")
        );
        invalidate.afterPropertyMutation();
        setDiscDayOpen(false);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to apply discount"); }
    finally { setDiscDayBusy(false); }
  };

  const removeDiscountDay = async (pid = discDayProp, date = discDayDate, end = "") => {
    if (!pid || !date) { toast.error("Pick a property and a date"); return; }
    const isRange = !!end && end > date;
    setDiscDayBusy(true);
    try {
      const { data } = await api.post("/pricing/discount/remove", {
        property_id: pid, date, ...(isRange ? { end_date: end } : {}),
      });
      if (!data.removed) {
        toast(data.message || "No price change is set on that day");
      } else {
        toast.success(
          `${isRange ? `${date} to ${end} (${data.nights} night${data.nights === 1 ? "" : "s"})` : date} back to ₹${data.rate?.toLocaleString("en-IN")}/night` +
          (data.pushed ? " · pushed to OTAs ✓" : data.routed ? " · sent to the pricing engine, booking sites update once it is applied" : "")
        );
        invalidate.afterPropertyMutation();
        setDiscDayOpen(false);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove the price change"); }
    finally { setDiscDayBusy(false); }
  };

  const buildWhatsAppUrl = (b) => {
    const propName = propMap[b.property_id] || "the property";
    const prop = properties.find(p => p.id === b.property_id);
    const fmt = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : d;
    const nights = b.check_in && b.check_out ? Math.round((new Date(b.check_out) - new Date(b.check_in)) / 86400000) : "";
    const bookingId = b.public_token || b.id || b.booking_id;
    const guidebookLine = (prop?.guidebook_enabled && bookingId)
      ? `\n\nYour digital guidebook (WiFi, check-in steps & local recommendations):\n${window.location.origin}/guide/${bookingId}`
      : "";
    const msg =
      `Hi ${b.guest_name}!\n\n` +
      `Your booking at *${propName}* is confirmed.\n\n` +
      `Check-in: ${fmt(b.check_in)}\n` +
      `Check-out: ${fmt(b.check_out)}\n` +
      `${nights} night${nights !== 1 ? "s" : ""}` +
      guidebookLine +
      `\n\nLooking forward to hosting you! Feel free to reach out if you need anything.`;
    const phone = (b.guest_phone || "").replace(/\D/g, "");
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  };

  const inputCls = "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-400 mt-1 focus:border-amber-400 focus:ring-amber-400";
  const selectTriggerCls = "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 mt-1";
  const btnCancel = "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 font-medium rounded-xl px-5 py-2.5 text-sm transition-colors";
  const btnPrimary = "bg-amber-400 text-white hover:bg-amber-500 font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors";

  if (loading) return (
    <div className="animate-pulse space-y-4">
      <div className="h-8 w-48 bg-slate-200 rounded" />
      <div className="h-96 bg-slate-100 rounded-2xl" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <OnboardingBanner pageKey="calendar" />
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-manrope text-2xl md:text-3xl font-semibold text-slate-900 dark:text-amber-400 tracking-tight">Bookings & Operations</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">{bookings.length} bookings total</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={openDiscountDay}
            className="flex items-center gap-1.5 px-3 min-h-[44px] rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 text-xs font-semibold transition-colors">
            <Tag size={13} weight="bold" /> Change price
          </button>
          <button onClick={() => { setBlockForm(f => ({ ...f, property_id: selectedPropertyId || f.property_id })); setBlockDialogOpen(true); }}
            className="flex items-center gap-1.5 px-3 min-h-[44px] rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 text-xs font-semibold transition-colors">
            <LockSimple size={13} weight="bold" /> Block Dates
          </button>
        </div>
      </div>

      {pendingCount > 0 && (() => {
        const pendingBookings = bookings.filter(b => b.status === "pending_approval");
        const first = pendingBookings[0];
        const fmt = d => d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "";
        const jumpToPending = () => {
          if (first?.check_in) setSelectedDate(new Date(first.check_in + "T00:00:00"));
        };
        return (
          <button onClick={jumpToPending} className="w-full text-left flex items-center gap-2.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-xl px-4 py-3 text-sm text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors">
            <WarningCircle size={18} weight="fill" className="text-amber-500 shrink-0" />
            <span className="flex-1">
              <strong>{pendingCount} booking request{pendingCount > 1 ? "s" : ""}</strong> waiting for approval
              {first?.check_in && <> · <span className="underline decoration-dotted">{first.guest_name ? `${first.guest_name}, ` : ""}{fmt(first.check_in)} → {fmt(first.check_out)}</span></>}
              {pendingCount > 1 && ` (+${pendingCount - 1} more)`}
            </span>
            <span className="text-amber-500 font-semibold text-xs shrink-0">Jump to week →</span>
          </button>
        );
      })()}

      {conflictCount > 0 && (
        <div className="flex items-center gap-2.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/40 rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-400">
          <WarningCircle size={18} weight="fill" className="text-red-500 shrink-0" />
          <span>
            <strong>{conflictCount} double booking conflict{conflictCount > 1 ? "s" : ""}</strong> detected from iCal sync.
            Scroll to the highlighted dates to resolve.
          </span>
        </div>
      )}

      {turnoverIssues.length > 0 && (
        <div className="flex items-start gap-2.5 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800/40 rounded-xl px-4 py-3 text-sm text-orange-700 dark:text-orange-400">
          <WarningCircle size={18} weight="fill" className="text-orange-500 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <p><strong>{turnoverIssues.length} same-day turnover{turnoverIssues.length > 1 ? "s" : ""} need{turnoverIssues.length > 1 ? "" : "s"} attention:</strong></p>
            {turnoverIssues.slice(0, 4).map((t, i) => (
              <p key={i} className="text-xs">
                {new Date(t.date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · {propMap[t.property_id] || ""} — {t.out} out → {t.in} in. {t.reason}.
              </p>
            ))}
            {turnoverIssues.length > 4 && <p className="text-xs">…and {turnoverIssues.length - 4} more.</p>}
          </div>
        </div>
      )}

      <Tabs defaultValue="calendar" onValueChange={v => v === "leads" && fetchLeads()}>
        <TabsList className="bg-slate-100 dark:bg-slate-700 w-full md:w-auto">
          <TabsTrigger value="calendar" className="flex-1 md:flex-none min-h-[44px] text-xs text-slate-600 dark:text-slate-300 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Calendar</TabsTrigger>
          <TabsTrigger value="leads" className="flex-1 md:flex-none min-h-[44px] text-xs text-slate-600 dark:text-slate-300 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Leads</TabsTrigger>
          <TabsTrigger value="reviews" className="flex-1 md:flex-none min-h-[44px] text-xs text-slate-600 dark:text-slate-300 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Reviews</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-4">
          <div className="grid md:grid-cols-[auto_1fr] gap-6">
            <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-2xl shadow-sm self-start" data-testid="booking-calendar">
              <CardContent className="p-4">
                {/* Airbnb-style month grid: each day shows the nightly rate (from
                    rate_calendar → updates with dynamic pricing) + booking status. */}
                {(() => {
                  const y = calMonth.getFullYear(), mo = calMonth.getMonth();
                  const firstDow = new Date(y, mo, 1).getDay();
                  const daysIn = new Date(y, mo + 1, 0).getDate();
                  const cells = [];
                  for (let i = 0; i < firstDow; i++) cells.push(null);
                  for (let d = 1; d <= daysIn; d++) cells.push(new Date(y, mo, d));
                  const todayStr = localDateStr(new Date());
                  return (
                    <div className="w-[min(100%,340px)]">
                      {/* Month nav */}
                      <div className="flex items-center justify-between mb-3 px-1">
                        <span className="font-manrope text-sm font-bold text-slate-800 dark:text-white">
                          {calMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                        </span>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setCalMonth(new Date(y, mo - 1, 1))}
                            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-base leading-none">‹</button>
                          <button onClick={() => setCalMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
                            className="px-2 min-h-[44px] text-[10px] font-semibold text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">Today</button>
                          <button onClick={() => setCalMonth(new Date(y, mo + 1, 1))}
                            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-base leading-none">›</button>
                        </div>
                      </div>
                      {/* Weekday header */}
                      <div className="grid grid-cols-7 gap-1 mb-1">
                        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(w => (
                          <div key={w} className="text-center text-[10px] font-semibold text-slate-400 py-0.5">{w}</div>
                        ))}
                      </div>
                      {/* Day grid */}
                      <div className="grid grid-cols-7 gap-1">
                        {cells.map((day, idx) => {
                          if (!day) return <div key={`b${idx}`} />;
                          const ds = localDateStr(day);
                          const isToday    = ds === todayStr;
                          const isSel      = ds === selectedStr;
                          const isBooked   = bookedDates.has(ds);
                          const isConflict = conflictDates.has(ds);
                          const isCheckout = checkoutDates.has(ds) && !isBooked;
                          const isBlocked  = hostBlockedDates.has(ds);
                          const isBuffer   = bufferDates.has(ds) && !isBooked && !isConflict;
                          const rate = rateForDay(ds);
                          const dayNote = notesForDate(ds).length > 0;
                          let tone = "bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 text-slate-700 dark:text-slate-200";
                          if (isConflict)      tone = "bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-300";
                          else if (isBooked)   tone = "bg-amber-100 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800/50 text-amber-700 dark:text-amber-300";
                          else if (isCheckout) tone = "bg-teal-50 dark:bg-teal-900/20 border-dashed border-teal-300 text-teal-700 dark:text-teal-300";
                          else if (isBlocked)  tone = "bg-slate-100 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 text-slate-400";
                          else if (isBuffer)   tone = "bg-blue-50 dark:bg-blue-900/20 border-dashed border-blue-200 text-blue-600 dark:text-blue-300";
                          return (
                            <button key={ds} onClick={() => setSelectedDate(day)} title={rate != null ? `${ds} · ${formatINR(rate)}/night` : ds}
                              className={`flex flex-col items-center justify-center h-12 rounded-lg border text-center transition-colors ${tone} ${isSel ? "ring-2 ring-slate-800 dark:ring-white" : isToday ? "ring-2 ring-amber-400" : ""}`}>
                              <span className="text-xs font-semibold leading-none">
                                {day.getDate()}
                                {dayNote && (
                                  <span className="ml-0.5 inline-block h-1 w-1 rounded-full bg-sky-500 align-top"
                                        aria-label="has a note" />
                                )}
                              </span>
                              {rate != null && (
                                <span className={`mt-0.5 text-[9px] font-mono leading-none whitespace-nowrap ${
                                  isBlocked ? "line-through opacity-60"
                                  : rateInfoForDay(ds)?.discounted ? "font-bold text-emerald-600 dark:text-emerald-400"
                                  : "opacity-80"}`}>
                                  {formatINR(rate)}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {/* Notes on the selected day. Outside the rate panel because that
                          panel only renders for a single property, while notes matter in
                          the all-properties view too. */}
                      {notesForDate(selectedStr).length > 0 && (
                        <div className="mt-3 rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 px-3 py-2">
                          <div className="text-[9px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                            {new Date(selectedStr + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · notes
                          </div>
                          {notesForDate(selectedStr).map((n, i) => (
                            <div key={i} className="mt-1">
                              {n.property && (
                                <div className="text-[9px] font-semibold text-slate-500 dark:text-slate-400">{n.property}</div>
                              )}
                              <div className="text-[11px] text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">
                                {n.note}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Selected day: price + discount controls in place */}
                      {rateContextProp && (() => {
                        const info = rateInfoForDay(selectedStr);
                        if (!info) return null;
                        const pid = rateContextProp.id || rateContextProp.property_id;
                        const pretty = new Date(selectedStr + "T00:00:00")
                          .toLocaleDateString("en-IN", { day: "numeric", month: "short" });
                        return (
                          <div className={`mt-3 rounded-xl border px-3 py-2.5 ${
                            info.discounted
                              ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20"
                              : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60"}`}>
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">{pretty}</span>
                              <span className="flex items-baseline gap-1.5">
                                {info.discounted && (
                                  <span className="text-[10px] font-mono text-slate-400 line-through">{formatINR(info.base)}</span>
                                )}
                                <span className={`text-sm font-bold font-mono ${
                                  info.discounted ? "text-emerald-600 dark:text-emerald-400" : "text-slate-700 dark:text-slate-200"}`}>
                                  {formatINR(info.rate)}
                                </span>
                                {info.discounted && info.pct > 0 && (
                                  <span className="text-[9px] font-bold text-emerald-700 dark:text-emerald-400">−{info.pct}%</span>
                                )}
                              </span>
                            </div>
                            <div className="mt-2 flex gap-2">
                              <button onClick={() => openDiscountForDay(selectedStr)} disabled={discDayBusy}
                                className="flex-1 text-xs font-semibold px-3 py-2 rounded-lg bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 transition disabled:opacity-50">
                                {info.discounted ? "Edit special price" : "Change price"}
                              </button>
                              {info.discounted && (
                                <button onClick={() => removeDiscountDay(pid, selectedStr)} disabled={discDayBusy}
                                  className="flex-1 text-xs font-semibold px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/50 transition disabled:opacity-50">
                                  {discDayBusy ? "Removing…" : "Remove discount"}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      {!rateContextProp && (
                        <p className="mt-2 text-[10px] text-slate-400 text-center">Select a property in the filter to see nightly rates per day.</p>
                      )}
                    </div>
                  );
                })()}
                {/* Legend */}
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                  <span className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                    <span className="w-3 h-3 rounded-full inline-block border-2 border-[#D4AF37]" /> Today
                  </span>
                  <span className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-amber-200 inline-block" /> Booked nights
                  </span>
                  <span className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-teal-100 border-2 border-dashed border-teal-400 inline-block" /> Checkout day
                  </span>
                  <span className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-red-200 inline-block" /> Conflict
                  </span>
                  <span className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-slate-200 inline-block" /> Blocked
                  </span>
                  {(selectedPropertyId ? (propBufferMap[selectedPropertyId] || 0) > 0 : Object.values(propBufferMap).some(v => v > 0)) && (
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                      <span className="w-3 h-3 rounded-full bg-blue-100 border border-dashed border-blue-300 inline-block" /> Cleaning gap
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                    <span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Ready for check-in
                  </span>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              {/* Week header */}
              <div className="flex items-center justify-between">
                <h3 className="font-manrope text-sm font-semibold text-slate-800 dark:text-white">
                  {weekDays[0].toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — {weekDays[6].toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </h3>
                <div className="flex items-center gap-1">
                  <button onClick={() => shiftWeek(-1)} className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-base leading-none">‹</button>
                  <button onClick={() => setSelectedDate(new Date())} className="px-2 min-h-[44px] text-[10px] font-semibold text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">Today</button>
                  <button onClick={() => shiftWeek(1)} className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-base leading-none">›</button>
                </div>
              </div>

              {/* Day rows */}
              {weekDays.map((day) => {
                const dayStr = localDateStr(day);
                const isToday = dayStr === localDateStr(new Date());
                const dayName = day.toLocaleDateString('en-IN', { weekday: 'short' });
                const dayNum = day.getDate();
                const dayMon = day.toLocaleDateString('en-IN', { month: 'short' });
                const bkgs = calBookings.filter(b => { try { return b.check_in <= dayStr && b.check_out > dayStr; } catch { return false; } });
                const isBufferDay = bufferDates.has(dayStr) && bkgs.length === 0;
                // Find which booking's buffer this day belongs to
                const bufferBooking = isBufferDay ? calBookings.find(b => {
                  try {
                    const buf = propById[b.property_id] ? effectiveBuffer(propById[b.property_id], b.check_in) : 0;
                    if (buf === 0) return false;
                    const day = new Date(dayStr + "T00:00:00");
                    const co = new Date(b.check_out + "T00:00:00");
                    const bufEnd = new Date(co.getTime() + buf * 86400000);
                    if (day >= co && day < bufEnd) return true;
                    const ci = new Date(b.check_in + "T00:00:00");
                    const preStart = new Date(ci.getTime() - buf * 86400000);
                    return day >= preStart && day < ci;
                  } catch { return false; }
                }) : null;
                const dayTurnovers = turnoverIssues.filter(t => t.date === dayStr);
                return (
                  <div key={dayStr} className={`rounded-xl border p-3 transition-colors ${isToday ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${isToday ? 'bg-amber-400 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                          {dayNum}
                        </div>
                        <div>
                          <p className={`text-xs font-semibold ${isToday ? 'text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-200'}`}>{dayName}</p>
                          <p className="text-[10px] text-slate-400">{dayMon}</p>
                        </div>
                        {bkgs.length > 0 && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 font-semibold px-1.5 py-0.5 rounded-full">{bkgs.length}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const info = rateInfoForDay(dayStr);
                          if (!info) return null;
                          return (
                            <button onClick={() => openDiscountForDay(dayStr)}
                              title={info.discounted
                                ? `${info.pct}% off · tap to change or remove the discount`
                                : info.special
                                  ? "Special price for this day · tap to change or remove it"
                                  : "Nightly rate · tap to change the price for this day"}
                              className={`flex items-center gap-1 rounded-lg px-1.5 py-1 transition-colors ${
                                info.discounted
                                  ? "bg-emerald-50 dark:bg-emerald-900/25 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
                                  : info.special
                                    ? "bg-amber-50 dark:bg-amber-900/25 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                                    : "hover:bg-slate-100 dark:hover:bg-slate-700"}`}>
                              {info.special && (
                                <span className="text-[10px] font-mono text-slate-400 line-through">{formatINR(info.base)}</span>
                              )}
                              <span className={`text-xs font-semibold font-mono ${
                                info.discounted
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : info.special
                                    ? "text-amber-700 dark:text-amber-400"
                                    : "text-slate-600 dark:text-slate-300"}`}>
                                {formatINR(info.rate)}
                              </span>
                              {info.discounted && info.pct > 0 && (
                                <span className="text-[9px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/50 rounded px-1 py-px">
                                  −{info.pct}%
                                </span>
                              )}
                            </button>
                          );
                        })()}
                        <button onClick={() => { setEditBooking(null); setForm({...defaultBookingForm(), check_in: dayStr, check_out: nextDayStr(dayStr), property_id: selectedPropertyId || ""}); setDialogOpen(true); }}
                          className="text-amber-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg min-h-[44px] min-w-[44px] inline-flex items-center justify-center transition-colors" data-testid="add-booking-button" title="Add direct booking" aria-label="Add direct booking">
                          <Plus size={14} weight="bold" />
                        </button>
                      </div>
                    </div>
                    {isBufferDay && bufferBooking && (
                      <div className="flex items-center gap-2 py-1.5 border-t border-blue-100 dark:border-blue-900/30 bg-blue-50/60 dark:bg-blue-950/20 rounded-lg px-1.5 mt-1">
                        <span className="w-2 h-2 rounded-full bg-blue-300 border border-dashed border-blue-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-blue-700 dark:text-blue-400">Cleaning gap</p>
                          <p className="text-[10px] text-blue-500 dark:text-blue-500 mt-0.5">
                            {dayStr < bufferBooking.check_in
                              ? `Before ${bufferBooking.guest_name || "booking"} checks in`
                              : `After ${bufferBooking.guest_name || "booking"}`} · guests can't book this day
                          </p>
                        </div>
                      </div>
                    )}
                    {dayTurnovers.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 py-1.5 border-t border-orange-100 dark:border-orange-900/30 bg-orange-50/60 dark:bg-orange-950/20 rounded-lg px-1.5 mt-1">
                        <WarningCircle size={12} weight="fill" className="text-orange-500 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-orange-700 dark:text-orange-400">
                            Same-day turnover · {t.out} out → {t.in} in
                          </p>
                          <p className="text-[10px] text-orange-600 dark:text-orange-500 mt-0.5">
                            {t.reason} · {propTimesMap[t.property_id]?.checkout || "11:00"} checkout, {propTimesMap[t.property_id]?.checkin || "14:00"} check-in
                          </p>
                        </div>
                      </div>
                    ))}
                    {bkgs.length > 0 ? bkgs.map(b => {
                      const isConflict = b.status === "conflict";
                      const isPending  = b.status === "pending_approval";
                      const needsReview = b.host_review === "pending" && !isConflict;
                      const isBlocked  = b.status === "blocked" && b.source === "host_block";
                      const isEvent    = b.booking_type === "event";
                      const isHighlighted = highlightId && (b.id || b.booking_id) === highlightId;
                      return (
                        <div key={b.id || b.booking_id} id={`bk-${b.id || b.booking_id}`}
                          className={`flex flex-wrap items-center justify-between py-1.5 border-t gap-2 ${
                            isConflict ? "border-red-100 dark:border-red-900/30 bg-red-50/50 dark:bg-red-950/20 rounded-lg px-1.5"
                            : isPending && isEvent ? "border-yellow-200 dark:border-yellow-800/40 bg-yellow-50/60 dark:bg-yellow-950/20 rounded-lg px-1.5"
                            : isPending ? "border-amber-100 dark:border-amber-900/30 bg-amber-50/50 dark:bg-amber-950/20 rounded-lg px-1.5"
                            : isEvent ? "border-yellow-200 dark:border-yellow-800/40 bg-yellow-50/40 dark:bg-yellow-950/10 rounded-lg px-1.5"
                            : isBlocked ? "border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-700/50 rounded-lg px-1.5"
                            : "border-slate-100 dark:border-slate-700"
                          } ${isHighlighted ? "ring-2 ring-teal-400 bg-teal-50/60 dark:bg-teal-900/20 rounded-lg px-1.5" : ""}`}>
                          <div className="min-w-0">
                            <p className={`text-xs font-semibold truncate flex items-center gap-1 ${isConflict ? "text-red-700 dark:text-red-400" : (isPending && isEvent) ? "text-yellow-700 dark:text-yellow-400" : isPending ? "text-amber-700 dark:text-amber-400" : isEvent ? "text-yellow-700 dark:text-yellow-500" : isBlocked ? "text-slate-500 dark:text-slate-400" : "text-slate-800 dark:text-white"}`}>
                              {(isConflict || isPending) && <WarningCircle size={11} weight="fill" className={isConflict ? "text-red-500 shrink-0" : "text-amber-500 shrink-0"} />}
                              {isEvent ? <Confetti size={11} className="text-yellow-500 shrink-0" weight="fill" /> : isBlocked ? <LockSimple size={11} className="text-slate-400 shrink-0" /> : <User size={11} className="text-slate-400 shrink-0" />}
                              {isBlocked ? (b.guest_name || "Blocked") : b.guest_name}
                            </p>
                            {isConflict && <p className="text-[10px] text-red-500 font-medium mt-0.5">Double booking — needs resolution</p>}
                            {isPending && isEvent  && <p className="text-[10px] text-yellow-600 dark:text-yellow-400 font-medium mt-0.5">Event request — awaiting approval</p>}
                            {isPending && !isEvent && <p className="text-[10px] text-amber-600 font-medium mt-0.5">Booking request — awaiting approval</p>}
                            {needsReview && <p className="text-[10px] text-emerald-700 dark:text-emerald-400 font-medium mt-0.5">New {(b.platform || "OTA")} booking, accept or report below</p>}
                            {isBlocked  && <p className="text-[10px] text-slate-400 mt-0.5">{b.check_in} → {b.check_out}</p>}
                            {!isBlocked && (
                              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                {isEvent && b.event_type
                                  ? <Badge variant="outline" className="text-[9px] px-1 py-0 bg-yellow-50 text-yellow-700 border-yellow-300 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-700/40 capitalize">{b.event_type}</Badge>
                                  : <Badge variant="outline" className={`text-[9px] px-1 py-0 ${isConflict ? "bg-red-50 text-red-500 border-red-200" : isPending ? "bg-amber-50 text-amber-600 border-amber-200" : "bg-slate-50 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600"}`}>{b.platform}</Badge>
                                }
                                <span className="text-[10px] text-slate-400">{propMap[b.property_id] || ""}</span>
                                <span className="text-[10px] text-slate-300">{b.check_in} → {b.check_out}</span>
                                {b.early_checkin_requested && (
                                  <Badge variant="outline" className="text-[9px] px-1 py-0 bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800/40">Early check-in requested</Badge>
                                )}
                                {b.late_checkout_requested && (
                                  <Badge variant="outline" className="text-[9px] px-1 py-0 bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800/40">Late checkout requested</Badge>
                                )}
                              </div>
                            )}
                            {!isBlocked && b.guest_phone && (
                              <a href={`tel:${b.guest_phone}`} className="flex items-center gap-1 mt-0.5 text-[10px] text-slate-400 hover:text-amber-500 transition-colors w-fit">
                                <Phone size={9} />{b.guest_phone}
                              </a>
                            )}
                            {!isBlocked && b.guest_id_url && (
                              <a href={b.guest_id_url} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 mt-0.5 text-[10px] text-teal-500 hover:text-teal-600 font-medium transition-colors w-fit">
                                <IdentificationCard size={10} weight="fill" />View guest ID
                              </a>
                            )}
                            {b.notes && (
                              <p className="mt-1 text-[10px] leading-snug text-slate-500 dark:text-slate-400 whitespace-pre-wrap border-l-2 border-amber-300 dark:border-amber-700/60 pl-2">
                                {b.notes}
                              </p>
                            )}
                            {!isBlocked && (b.amount > 0 || b.total_amount > 0 || b.invoice_available) && (() => {
                              // The booking itself decides which document it gets: a tax invoice
                              // only when the host is registered and tax was actually charged,
                              // otherwise a plain invoice with no tax rows. Saying which one it
                              // is up front saves opening the PDF to find out. A tax invoice
                              // cannot be issued before the stay is confirmed, so an unconfirmed
                              // booking still offers the proforma rather than nothing at all.
                              // Revenue-hidden staff get only the server's verdict, not the figures.
                              const withGst = b.invoice_gst ?? (!b.gst_exempt && ((b.gst_amount || b.tax_amount) > 0));
                              const confirmed = b.status === "confirmed" || b.status === "completed";
                              const pill = "inline-flex items-center gap-1 px-2.5 py-1.5 min-h-[32px] sm:min-h-0 sm:py-1 rounded-lg border text-[11px] font-semibold transition-colors";
                              return (
                                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                  {confirmed ? (
                                    <a href={`${API_URL}/book/booking/${b.public_token || b.id || b.booking_id}/invoice?v=2`} target="_blank" rel="noopener noreferrer"
                                      className={`${pill} border-amber-200 dark:border-amber-800/50 text-amber-700 dark:text-amber-400 bg-amber-50/70 dark:bg-amber-950/20 hover:bg-amber-100 dark:hover:bg-amber-900/30`}>
                                      🧾 {withGst ? "GST invoice" : "Invoice (no GST)"}
                                    </a>
                                  ) : (
                                    <a href={`${API_URL}/book/booking/${b.public_token || b.id || b.booking_id}/proforma?v=2`} target="_blank" rel="noopener noreferrer"
                                      className={`${pill} border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700`}>
                                      🧾 Proforma invoice
                                    </a>
                                  )}
                                </div>
                              );
                            })()}
                            {!isBlocked && b.check_in === dayStr && propById[b.property_id]?.ready_for_checkin && (
                              <span className="flex items-center gap-1 mt-0.5 text-[10px] font-semibold text-green-600 dark:text-green-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />✓ Ready for check-in
                              </span>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
                              {isBlocked ? (
                                <button onClick={() => setDeleteConfirmId(b.id || b.booking_id)}
                                  className="text-[10px] min-h-[44px] px-1 text-slate-400 hover:text-red-500 flex items-center gap-0.5">
                                  <Prohibit size={10} />Unblock
                                </button>
                              ) : isConflict ? (
                                <button onClick={() => handleResolveConflict(b.id || b.booking_id)}
                                  className="text-[10px] min-h-[44px] px-1 text-white bg-red-500 hover:bg-red-600 rounded-md px-1.5 py-0.5 font-semibold transition-colors">
                                  Cancel conflict
                                </button>
                              ) : isPending ? (
                                null
                              ) : (
                                <>
                                  <button onClick={() => openEditBooking(b)} className="text-[10px] min-h-[44px] px-1 text-slate-400 hover:text-amber-500 flex items-center gap-0.5"><PencilSimple size={10} />Edit</button>
                                  <button
                                    onClick={() => sendReviewRequest(b.id || b.booking_id)}
                                    disabled={sendingReviewId === (b.id || b.booking_id)}
                                    title="Send review request"
                                    className="text-[10px] min-h-[44px] px-1 text-slate-400 hover:text-teal-500 disabled:opacity-50 flex items-center gap-0.5"
                                  >
                                    {sendingReviewId === (b.id || b.booking_id)
                                      ? <Spinner size={10} className="animate-spin" />
                                      : <ShareNetwork size={10} />}
                                    Review
                                  </button>
                                  <button
                                    onClick={() => sendWhatsAppConfirmation(b.id || b.booking_id)}
                                    disabled={sendingWaId === (b.id || b.booking_id) || !b.guest_phone}
                                    title={b.guest_phone ? "Send booking confirmation via WhatsApp" : "No phone number"}
                                    className={`text-[10px] min-h-[44px] px-1 flex items-center gap-0.5 disabled:opacity-40 ${b.guest_phone ? "text-slate-400 hover:text-green-500" : "text-slate-300"}`}
                                  >
                                    {sendingWaId === (b.id || b.booking_id) ? <Spinner size={10} className="animate-spin" /> : <WhatsappLogo size={10} />}
                                    WhatsApp
                                  </button>
                                  <a
                                    href={buildWhatsAppUrl(b)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open WhatsApp with pre-filled message"
                                    className={`text-[10px] min-h-[44px] px-1 flex items-center gap-0.5 ${b.guest_phone ? "text-slate-300 hover:text-green-400" : "text-slate-200 hover:text-slate-300"}`}
                                  >
                                    <WhatsappLogo size={9} />Manual{!b.guest_phone && <span className="text-[8px]">*</span>}
                                  </a>
                                  {b.booking_type !== "event" && (
                                    <button onClick={() => openExtend(b)} title="Offer this guest a discounted extension"
                                      className="text-[10px] min-h-[44px] px-1 text-slate-400 hover:text-amber-600 flex items-center gap-0.5">
                                      <CalendarPlus size={10} />Extend
                                    </button>
                                  )}
                                  {!b.aiosell_booking_id && (
                                    <button onClick={() => setDeleteConfirmId(b.id || b.booking_id)} className="text-[10px] min-h-[44px] px-1 text-slate-400 hover:text-red-500">Delete</button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          {(isPending || needsReview) && (
                            <div className="basis-full mt-1.5 pt-1.5 border-t border-dashed border-slate-200 dark:border-slate-600">
                              {reportId === (b.id || b.booking_id) ? (
                                <div className="flex flex-col gap-2">
                                  <textarea
                                    value={reportReason}
                                    onChange={e => setReportReason(e.target.value)}
                                    placeholder="What is wrong? For example: flat already occupied, wrong dates, cannot host this guest."
                                    rows={2}
                                    className="w-full text-base sm:text-xs px-3 py-2 rounded-xl border border-red-200 dark:border-red-800 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-red-400"
                                  />
                                  <div className="flex flex-col sm:flex-row gap-2">
                                    <button onClick={() => reviewOtaBooking(b, "reject")} disabled={!reportReason.trim()}
                                      className="w-full sm:w-auto min-h-[44px] px-4 text-sm text-white bg-red-500 hover:bg-red-600 rounded-xl font-semibold disabled:opacity-50">
                                      Send report, cancel this booking
                                    </button>
                                    <button onClick={() => { setReportId(null); setReportReason(""); }}
                                      className="w-full sm:w-auto min-h-[44px] px-4 text-sm text-slate-600 dark:text-slate-300 rounded-xl border border-slate-200 dark:border-slate-600">
                                      Back
                                    </button>
                                  </div>
                                </div>
                              ) : declineConfirmId === (b.id || b.booking_id) ? (
                                <div className="flex flex-col sm:flex-row gap-2">
                                  <button onClick={() => handleDeclineBooking(b)}
                                    className="w-full sm:w-auto min-h-[44px] px-4 text-sm text-white bg-red-500 hover:bg-red-600 rounded-xl font-semibold">
                                    Yes, decline this request
                                  </button>
                                  <button onClick={() => setDeclineConfirmId(null)}
                                    className="w-full sm:w-auto min-h-[44px] px-4 text-sm text-slate-600 dark:text-slate-300 rounded-xl border border-slate-200 dark:border-slate-600">
                                    Back
                                  </button>
                                </div>
                              ) : !mayDecideBookings ? (
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                  Only the host can {isPending ? "approve or decline" : "accept or report"} this booking.
                                </p>
                              ) : (
                                <div className="flex flex-col sm:flex-row gap-2">
                                  <button onClick={() => isPending ? handleApproveBooking(b) : reviewOtaBooking(b, "accept")}
                                    className="w-full sm:w-auto min-h-[44px] px-4 text-sm text-white bg-green-600 hover:bg-green-700 rounded-xl font-semibold">
                                    {isPending ? "Approve" : "Accept booking"}
                                  </button>
                                  <button onClick={() => { if (isPending) setDeclineConfirmId(b.id || b.booking_id); else { setReportId(b.id || b.booking_id); setReportReason(""); } }}
                                    className="w-full sm:w-auto min-h-[44px] px-4 text-sm text-red-600 dark:text-red-400 rounded-xl border border-red-200 dark:border-red-800 font-semibold">
                                    {isPending ? "Decline" : "Report a problem"}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }) : (!isBufferDay && (
                      <p className="text-[11px] text-slate-300 dark:text-slate-600 pt-1">No bookings</p>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="leads" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-800 dark:text-white">Booking Enquiries</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Guests who filled in their details but haven't completed payment yet (last 30 days).</p>
            </div>
            <button onClick={fetchLeads} className="text-xs text-amber-600 hover:text-amber-700 font-medium">Refresh</button>
          </div>
          {leadsLoading && <p className="text-slate-400 text-sm text-center py-12">Loading…</p>}
          {!leadsLoading && leads.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <ClockCounterClockwise size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">No pending enquiries in the last 30 days.</p>
              <p className="text-xs mt-1">When a guest fills in their details but doesn't pay, they'll appear here.</p>
            </div>
          )}
          {(selectedPropertyId ? leads.filter(l => l.property_id === selectedPropertyId) : leads).map(lead => {
            const minsAgo = Math.floor((Date.now() - new Date(lead.created_at)) / 60000);
            const timeAgo = minsAgo < 60 ? `${minsAgo}m ago` : minsAgo < 1440 ? `${Math.floor(minsAgo/60)}h ago` : `${Math.floor(minsAgo/1440)}d ago`;
            const alreadyNudged = !!lead.last_nudge_at;
            return (
              <div key={lead.booking_id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-800 dark:text-white">{lead.guest_name}</p>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-semibold">Unpaid</span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{lead.property_name} · {lead.check_in} → {lead.check_out} · {lead.nights} nights</p>
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">₹{(lead.amount || 0).toLocaleString("en-IN")}</p>
                    <div className="flex items-center gap-3 pt-0.5">
                      {lead.guest_phone && <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1"><Phone size={11} />{lead.guest_phone}</span>}
                      {lead.guest_email && <span className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[180px]">{lead.guest_email}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-[11px] text-slate-400">{timeAgo}</span>
                    <div className="flex gap-2">
                      {lead.guest_phone && (
                        <a href={`tel:${lead.guest_phone}`}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-medium rounded-lg transition">
                          <Phone size={12} weight="bold" /> Call
                        </a>
                      )}
                      {lead.guest_phone && (
                        <button
                          onClick={() => nudgeLead(lead.booking_id)}
                          disabled={nudgingId === lead.booking_id}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition">
                          {nudgingId === lead.booking_id
                            ? <Spinner size={12} className="animate-spin" />
                            : <WhatsappLogo size={12} weight="bold" />}
                          {alreadyNudged ? "Re-nudge" : "Send Reminder"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="reviews" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setReviewDialogOpen(true)} className="bg-amber-400 text-white hover:bg-amber-500 font-semibold rounded-xl px-3 py-2 text-xs flex items-center gap-1.5 transition-colors shadow-sm" data-testid="add-review-button">
              <Plus size={14} weight="bold" /> Add Review
            </button>
          </div>
          {(selectedPropertyId ? reviews.filter(r => r.property_id === selectedPropertyId) : reviews).map(r => (
            <div key={r.id} className="bg-white dark:bg-slate-800 border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm text-slate-800 dark:text-white font-semibold">{r.guest_name}</p>
                    {r.source === "guest_link" && (
                      <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400 border border-teal-200 dark:border-teal-800/40">
                        <CheckCircle size={10} weight="fill" /> Verified Guest
                      </span>
                    )}
                    {r.is_external && OTA_LABELS[r.source] && (
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${OTA_COLORS[r.source] || "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600"}`}>
                        {OTA_LABELS[r.source]}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{propMap[r.property_id] || ""} · {formatDate(r.date)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={14} weight={s <= r.rating ? "fill" : "regular"} className={s <= r.rating ? "text-amber-400" : "text-slate-200"} />
                    ))}
                  </div>
                  <button onClick={() => handleDeleteReview(r.id)} title="Delete review"
                    className="p-1 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
              {r.review_text && <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">{r.review_text}</p>}
              {r.channex_review_id && <ReviewReply review={r} />}
            </div>
          ))}
          {(selectedPropertyId ? reviews.filter(r => r.property_id === selectedPropertyId) : reviews).length === 0 && <p className="text-slate-400 text-sm text-center py-12">No reviews yet.</p>}
        </TabsContent>
      </Tabs>

      {/* Booking Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-md shadow-xl">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white">{editBooking ? "Edit Booking" : "Direct Booking"}</DialogTitle>
            <DialogDescription className="text-slate-300">Enter booking details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Property</Label>
              {!editBooking && selectedPropertyId ? (
                <p className="mt-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-sm text-slate-800 dark:text-slate-100" data-testid="booking-property-fixed">
                  {propMap[selectedPropertyId] || "Selected property"}
                </p>
              ) : (
              <Select value={form.property_id} onValueChange={v => handleFormChange("property_id", v)}>
                <SelectTrigger className={selectTriggerCls}><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name} {p.nightly_rate ? `— ${formatINR(p.nightly_rate)}/night` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Guest Name</Label>
                <Input value={form.guest_name} onChange={e => setForm(f => ({...f, guest_name: e.target.value}))} className={inputCls} data-testid="guest-name-input" />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Guest Phone</Label>
                <Input type="tel" placeholder="+91 98765 43210" value={form.guest_phone} onChange={e => setForm(f => ({...f, guest_phone: e.target.value}))} className={inputCls} />
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 px-3 py-1">
              <IdentificationCard size={14} className="text-slate-400 shrink-0" />
              <span className="text-xs text-slate-600 dark:text-slate-300 flex-1">Guest ID <span className="text-slate-400">(main guest)</span></span>
              {form.guest_id_url && (
                <a href={form.guest_id_url} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-amber-600 font-semibold hover:underline shrink-0 px-1">View</a>
              )}
              <label className="text-xs font-semibold text-amber-600 cursor-pointer shrink-0 px-2 min-h-[44px] flex items-center" data-testid="main-guest-id-upload">
                {guestIdUploading === "main" ? "Uploading…" : form.guest_id_url ? "Replace" : "Upload"}
                <input type="file" accept="image/*,.pdf" className="hidden"
                  onChange={e => uploadMainGuestId(e.target.files?.[0])} />
              </label>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Guest State <span className="text-slate-400 font-normal">(for GST)</span></Label>
                <button type="button" onClick={detectState} disabled={detectingState}
                  className="flex items-center gap-1 text-[10px] text-amber-600 hover:text-amber-700 font-semibold disabled:opacity-50 transition-colors">
                  {detectingState ? <><Spinner size={10} className="animate-spin" />Detecting…</> : <><NavigationArrow size={10} weight="fill" />Auto-detect</>}
                </button>
              </div>
              <Input value={form.guest_state} onChange={e => setForm(f => ({...f, guest_state: e.target.value}))} placeholder="e.g. Maharashtra" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Check-in</Label>
                <Input type="date" value={form.check_in} onChange={e => handleFormChange("check_in", e.target.value)} className={inputCls} />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Check-out</Label>
                <Input type="date" value={form.check_out} onChange={e => handleFormChange("check_out", e.target.value)} className={inputCls} />
              </div>
            </div>

            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">
                Notes <span className="text-slate-400 font-normal">(payment, requests, anything you want to remember)</span>
              </Label>
              <textarea
                value={form.notes}
                onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                rows={3}
                maxLength={2000}
                data-testid="booking-notes-input"
                placeholder="e.g. Paid 15,000 advance by UPI, balance 35,000 on arrival. Late check-in around 11pm."
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:border-amber-400 resize-y"
              />
              <p className="text-[10px] text-slate-400 mt-1">Only you and your team see this. Guests never do.</p>
            </div>

            {/* Live Pricing Breakdown */}
            {(() => {
              const bd = computeBreakdown(form.property_id, form.check_in, form.check_out, form.custom_price ? form.amount : "", !!form.charge_gst);
              return bd ? (
                <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-xl p-3 text-xs space-y-1.5">
                  <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-2">Pricing Breakdown</p>
                  <div className="flex justify-between text-slate-600 dark:text-slate-300">
                    <span>{bd.nights} night{bd.nights > 1 ? "s" : ""} {bd.rateVaries ? `(${formatINR(bd.minRate)} to ${formatINR(bd.maxRate)}/night)` : `× ${formatINR(bd.rate)}/night`}</span>
                    <span className="font-semibold">{formatINR(bd.subtotal)}</span>
                  </div>
                  {bd.gstRate > 0 && (
                    <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
                      <span>GST ({bd.gstRate}%) on accommodation</span>
                      <span>+ {formatINR(bd.gstAmt)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-slate-800 dark:text-slate-100 border-t border-amber-200 dark:border-amber-800/40 pt-1.5 mt-1">
                    <span>Guest Pays Total</span>
                    <span>{formatINR(bd.total)}</span>
                  </div>
                  {bd.gstRate > 0
                    ? <p className="text-[10px] text-slate-400 dark:text-slate-500">Host revenue (excl. GST): {formatINR(bd.subtotal)}</p>
                    : <p className="text-[10px] text-slate-400 dark:text-slate-500">No GST. This is the full and final price, invoice without GST.</p>}
                  <p className="text-[10px] text-slate-400 dark:text-slate-500">
                    {bd.hostSet ? "Using the price you entered for these dates." : "From this property's rates for these dates. For a discount, tick 'Give this guest a different price' below."}
                  </p>
                </div>
              ) : (
                // Never hide the price silently: say exactly what is missing.
                <p className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-xl px-3 py-2" data-testid="preview-needs-dates">
                  {!form.property_id ? "Select the property above to see the price."
                    : (!form.check_in || !form.check_out || form.check_out <= form.check_in) ? "Pick a check-out after check-in to see the price."
                    : "No rate is set for these dates. Tick 'Give this guest a different price' to enter one."}
                </p>
              );
            })()}

            <div className={`grid gap-3 ${editBooking ? "grid-cols-2" : "grid-cols-1"}`}>
              <div>
                <label className="flex items-start gap-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 px-3 py-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.custom_price} data-testid="booking-custom-price"
                    onChange={e => {
                      const on = e.target.checked;
                      setForm(f => {
                        if (on) return { ...f, custom_price: true };
                        const bd = computeBreakdown(f.property_id, f.check_in, f.check_out, "");
                        return { ...f, custom_price: false, amount: bd ? bd.subtotal : "" };
                      });
                    }}
                    className="mt-0.5 accent-amber-500" />
                  <span className="text-[11px] text-slate-600 dark:text-slate-300 leading-tight">
                    Give this guest a different price <span className="text-slate-400">(e.g. a discount). Off: the property's rates for these dates.</span>
                  </span>
                </label>
                {form.custom_price && (
                  <div className="mt-2">
                    <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Total price for the stay (INR)</Label>
                    <Input type="number" min="0" inputMode="decimal" value={form.amount} data-testid="booking-custom-amount"
                      onChange={e => setForm(f => ({...f, amount: e.target.value}))} className={inputCls} />
                  </div>
                )}
              </div>
              {/* + is always a direct booking; the platform is only shown when editing */}
              {editBooking && (
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Platform</Label>
                <Select value={form.platform} onValueChange={v => setForm(f => ({...f, platform: v}))}>
                  <SelectTrigger className={selectTriggerCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                    {PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Advance paid (INR) <span className="text-slate-400 font-normal">shown on the invoice with the balance due</span></Label>
              <Input type="number" min="0" inputMode="decimal" value={form.amount_paid} onChange={e => setForm(f => ({...f, amount_paid: e.target.value}))} className={inputCls} />
            </div>
            {user?.gst_number && (
              <label className="flex items-start gap-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 px-3 py-2 cursor-pointer">
                <input type="checkbox" checked={!!form.charge_gst} data-testid="booking-charge-gst"
                  onChange={e => setForm(f => ({...f, charge_gst: e.target.checked}))}
                  className="mt-0.5 accent-amber-500" />
                <span className="text-[11px] text-slate-600 dark:text-slate-300 leading-tight">
                  Charge GST on top <span className="text-slate-400">(GST invoice). Off: the price above is final, invoice without GST.</span>
                </span>
              </label>
            )}
            {form.charge_gst && (
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">GST Supply Type</Label>
              <Select value={form.supply_type} onValueChange={v => setForm(f => ({...f, supply_type: v}))}>
                <SelectTrigger className={selectTriggerCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                  <SelectItem value="intrastate">Intrastate (CGST + SGST)</SelectItem>
                  <SelectItem value="interstate">Interstate (IGST)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-start gap-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 px-3 py-2 cursor-pointer">
                <input type="checkbox" checked={!!form.early_checkin_requested}
                  onChange={e => setForm(f => ({...f, early_checkin_requested: e.target.checked}))}
                  className="mt-0.5 accent-amber-500" />
                <span className="text-[11px] text-slate-600 dark:text-slate-300 leading-tight">Early check-in requested</span>
              </label>
              <label className="flex items-start gap-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 px-3 py-2 cursor-pointer">
                <input type="checkbox" checked={!!form.late_checkout_requested}
                  onChange={e => setForm(f => ({...f, late_checkout_requested: e.target.checked}))}
                  className="mt-0.5 accent-amber-500" />
                <span className="text-[11px] text-slate-600 dark:text-slate-300 leading-tight">Late checkout requested</span>
              </label>
            </div>
            {/* Per-guest government IDs. Indian rules want a record for EVERY guest,
                not just whoever the booking is in the name of. */}
            <div className="border-t border-slate-200 dark:border-slate-600 pt-3">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Guest IDs</Label>
                <span className="text-[10px] text-slate-400">{form.guest_ids.length + (form.guest_id_url ? 1 : 0)} on file</span>
              </div>
              {form.guest_ids.map((g, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <Input value={g.name} placeholder="Guest name"
                    onChange={e => updateGuestId(i, { name: e.target.value })}
                    className={`${inputCls} flex-1`} data-testid={`guest-id-name-${i}`} />
                  {g.url ? (
                    <a href={g.url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-amber-600 font-semibold hover:underline shrink-0 px-2">View →</a>
                  ) : (
                    <label className="text-xs font-semibold text-amber-600 cursor-pointer shrink-0 px-2 py-2 min-h-[44px] flex items-center">
                      {guestIdUploading === i ? "Uploading…" : "Upload"}
                      <input type="file" accept="image/*,.pdf" className="hidden"
                        onChange={e => uploadGuestId(i, e.target.files?.[0])} />
                    </label>
                  )}
                  <button type="button" onClick={() => removeGuestId(i)}
                    className="text-xs text-slate-400 hover:text-red-500 shrink-0 min-h-[44px] px-2"
                    aria-label="Remove this guest">✕</button>
                </div>
              ))}
              <button type="button" onClick={addGuestId}
                className="text-xs font-semibold text-amber-600 hover:underline min-h-[44px]"
                data-testid="add-guest-id">+ Add another guest</button>
              <p className="text-[10px] text-slate-400 mt-1">Everyone else staying, each with their own ID. The main guest's ID goes at the top.</p>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className={btnCancel}>Cancel</button>
            <button onClick={handleSaveBooking} className={btnPrimary} data-testid="save-booking-button">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Dates Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-md shadow-xl">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white flex items-center gap-2">
              <LockSimple size={16} weight="bold" className="text-slate-500" /> {blockMode === "unblock" ? "Unblock Dates" : "Block Dates"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {blockMode === "unblock"
                ? "Reopen a date range so guests can book it again on every connected site."
                : "Block a date range for maintenance, personal use, or any reason. Guests won't be able to book these dates."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 dark:bg-slate-700 p-1" role="tablist">
              {[["block", "Block"], ["unblock", "Unblock"]].map(([m, l]) => (
                <button key={m} type="button" role="tab" aria-selected={blockMode === m} data-testid={`block-mode-${m}`}
                  onClick={() => setBlockMode(m)}
                  className={`min-h-[40px] rounded-lg text-sm font-semibold outline-none ring-0 focus:outline-none focus:ring-0 ${blockMode === m ? "bg-amber-400 text-white shadow" : "text-slate-500 dark:text-slate-300"}`}>
                  {l}
                </button>
              ))}
            </div>
            {blockMode === "unblock" && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Reopens these dates on Airbnb, Booking.com and every connected site, including dates closed on the site itself.</p>
            )}
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Property</Label>
              <Select value={blockForm.property_id} onValueChange={v => setBlockForm(f => ({...f, property_id: v}))}>
                <SelectTrigger className={selectTriggerCls}><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">From</Label>
                <Input type="date" value={blockForm.check_in} onChange={e => setBlockForm(f => ({...f, check_in: e.target.value}))} className={inputCls} />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">To</Label>
                <Input type="date" value={blockForm.check_out} onChange={e => setBlockForm(f => ({...f, check_out: e.target.value}))} className={inputCls} />
              </div>
            </div>
            {blockMode === "block" && (
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Reason</Label>
              <Select value={blockForm.label} onValueChange={v => setBlockForm(f => ({...f, label: v}))}>
                <SelectTrigger className={selectTriggerCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                  {BLOCK_LABELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            {/* Only blocks made in HostFolio can be unblocked here. Guest bookings from the
                OTAs are never listed: their dates reopen only when the guest cancels. */}
            {(() => {
              const today = localDateStr(new Date());
              const mine = bookings.filter(b => b.status === "blocked" && b.source === "host_block"
                && b.check_out >= today && (!blockForm.property_id || b.property_id === blockForm.property_id))
                .sort((a, b) => a.check_in.localeCompare(b.check_in));
              if (!mine.length) return null;
              return (
                <div className="border-t border-slate-200 dark:border-slate-600 pt-3" data-testid="existing-blocks">
                  <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Blocked in HostFolio</Label>
                  {mine.map(b => (
                    <div key={b.id} className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-600 dark:text-slate-300 flex-1">
                        {formatDate(b.check_in)} to {formatDate(b.check_out)}
                        {!blockForm.property_id && <span className="text-slate-400"> · {propMap[b.property_id] || ""}</span>}
                      </span>
                      <button type="button" onClick={() => setDeleteConfirmId(b.id)}
                        className="text-xs font-semibold text-red-500 hover:text-red-600 min-h-[44px] px-2">Unblock</button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <button onClick={() => setBlockDialogOpen(false)} className={btnCancel}>Cancel</button>
            <button onClick={blockMode === "unblock" ? () => handleUnblockDates(false) : handleBlockDates} disabled={savingBlock} className={btnPrimary}
              data-testid="block-submit">
              {savingBlock ? "Saving…" : blockMode === "unblock" ? "Unblock Dates" : "Block Dates"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Second verification: a guest holds dates the host is reopening */}
      <Dialog open={!!bookedOverlap} onOpenChange={(o) => !o && setBookedOverlap(null)}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-sm shadow-xl" data-testid="unblock-booked-confirm">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white">A guest has booked these dates</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              Reopening lets another guest book the same nights, which can cause a double booking.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            {(bookedOverlap || []).map((b, i) => (
              <p key={i} className="text-xs text-slate-700 dark:text-slate-200">
                <span className="font-semibold">{b.guest_name}</span>
                {b.platform && <span className="text-slate-500"> via {b.platform}</span>}
                <span className="text-slate-500"> · {formatDate(b.check_in)} to {formatDate(b.check_out)}</span>
              </p>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setBookedOverlap(null)} className={btnCancel}>Keep closed</button>
            <button onClick={() => handleUnblockDates(true)} disabled={savingBlock}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors">
              Reopen anyway
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(o) => !o && setDeleteConfirmId(null)}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-sm shadow-xl">
          <DialogHeader>
            {(() => {
              const t = bookings.find(x => (x.id || x.booking_id) === deleteConfirmId);
              const blk = t && t.status === "blocked" && t.source === "host_block";
              return (<>
                <DialogTitle className="font-manrope text-slate-900 dark:text-white">{blk ? "Unblock these dates?" : "Delete this booking?"}</DialogTitle>
                <DialogDescription className="text-slate-500 dark:text-slate-400">
                  {blk ? `${t.check_in} to ${t.check_out} reopen for booking on Airbnb, Booking.com and every other connected site.` : "This can't be undone, and the dates reopen on every connected site."}
                </DialogDescription>
              </>);
            })()}
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setDeleteConfirmId(null)} className={btnCancel}>Cancel</button>
            <button onClick={() => handleDeleteBooking(deleteConfirmId)}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors">
              {(() => { const t = bookings.find(x => (x.id || x.booking_id) === deleteConfirmId); return t && t.source === "host_block" ? "Unblock" : "Delete"; })()}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change price Dialog (one day or a range) */}
      <Dialog open={discDayOpen} onOpenChange={setDiscDayOpen}>
        {/* onOpenAutoFocus off: iOS pops the native picker for the Property select the moment it gets focus */}
        <DialogContent onOpenAutoFocus={e => e.preventDefault()} className="bg-white dark:bg-slate-800 border-slate-200 max-w-md shadow-xl">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white">Change price</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              Pick a date, or add an Until date to change a range. Type a price, higher or lower, or use the discount slider. Only those dates change and they are pushed to your booking sites.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Property</label>
              <select value={discDayProp} onChange={e => setDiscDayProp(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-300">
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Date</label>
              <input type="date" min={localDateStr(new Date())} value={discDayDate}
                onChange={e => setDiscDayDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 min-h-[44px] text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-300" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Until <span className="font-normal text-slate-400">(optional, for a range of dates)</span></label>
              <input type="date" min={discDayDate || localDateStr(new Date())} value={discDayEnd}
                onChange={e => setDiscDayEnd(e.target.value)}
                className="mt-1 w-full px-3 py-2 min-h-[44px] text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-300" />
              {discDayEnd && discDayEnd > discDayDate && (
                <p className="mt-1 text-[11px] text-slate-400">Every open night from {discDayDate} to {discDayEnd} gets this price. Booked nights are left alone.</p>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Price for this date (₹)</label>
              <input type="number" inputMode="numeric" min={100} step={1} value={discDayExact}
                onChange={e => setDiscDayExact(e.target.value)}
                placeholder={`Normal rate ${formatINR(properties.find(x => x.id === discDayProp)?.nightly_rate || 0)} · type a new price`}
                className="mt-1 w-full px-3 py-2.5 text-base sm:text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-300" />
              <p className="mt-1 text-[11px] text-slate-400">Higher or lower, this is what Airbnb shows. Booking sites with a higher commission get their markup on top automatically.</p>
            </div>
            <div className={Number(discDayExact) > 0 ? "opacity-40 pointer-events-none" : ""}>
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Or a discount instead</label>
                <span className="text-sm font-bold text-slate-800 dark:text-white">
                  {discDayPct === 0 ? "No discount" : `−${discDayPct}%`}
                </span>
              </div>
              <input type="range" min={0} max={50} step={1} value={discDayPct}
                onChange={e => setDiscDayPct(Number(e.target.value))}
                className="w-full accent-black dark:accent-white cursor-pointer mt-1" />
              {(() => {
                const p = properties.find(x => x.id === discDayProp);
                const orig = p?.nightly_rate || 0;
                const floor = Number(p?.min_price) || 0;
                // A manual discount is deliberate, so it is NOT capped at the price floor.
                // That floor only guards the automated pricing engines.
                const nw = Math.max(1, Math.round(orig * (1 - discDayPct / 100)));
                const belowFloor = floor > 0 && nw < floor;
                if (!orig) return null;
                return discDayPct === 0 ? (
                  <div className="mt-1.5 text-xs">
                    <span className="text-base font-bold text-slate-700 dark:text-slate-200">₹{orig.toLocaleString("en-IN")}</span>
                    <span className="text-slate-400">/night · full rate, any discount on this day is cleared</span>
                  </div>
                ) : (
                  <div className="mt-1.5 text-xs">
                    <span className="text-slate-400 line-through mr-1.5">₹{orig.toLocaleString("en-IN")}</span>
                    <span className="text-base font-bold text-emerald-600 dark:text-emerald-400">₹{nw.toLocaleString("en-IN")}</span>
                    <span className="text-slate-400">/night</span>
                    {belowFloor && (
                      <div className="mt-1.5 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                        Below your ₹{floor.toLocaleString("en-IN")} price floor. That floor still guards automatic pricing, this discount applies to this date only.
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            {(() => {
              const p = properties.find(x => x.id === discDayProp);
              const isRange = !!discDayEnd && discDayEnd > discDayDate;
              const inRange = (p?.rate_calendar || []).filter(e => e.rate && e.type !== "booked" &&
                e.date >= discDayDate && e.date <= (isRange ? discDayEnd : discDayDate));
              const entry = inRange[0];
              return entry ? (
                <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    {isRange ? (
                      <>{inRange.length} night{inRange.length === 1 ? "" : "s"} in this range already have a special rate.</>
                    ) : (
                      <>This day already has a special rate:{" "}
                        <span className="font-bold text-emerald-700 dark:text-emerald-400">₹{Number(entry.rate).toLocaleString("en-IN")}</span>
                        {entry.discount_pct > 0 ? ` (${entry.discount_pct}% off)` : ""}</>
                    )}
                  </div>
                  <button onClick={() => removeDiscountDay(discDayProp, discDayDate, discDayEnd)} disabled={discDayBusy}
                    className="shrink-0 w-full sm:w-auto text-xs font-semibold px-3 py-2.5 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/50 transition disabled:opacity-50">
                    Back to normal rate
                  </button>
                </div>
              ) : null;
            })()}
          </div>
          <DialogFooter>
            <button onClick={() => setDiscDayOpen(false)} className={btnCancel}>Cancel</button>
            <button onClick={() => Number(discDayExact) > 0 ? applyDiscountDay() : discDayPct === 0 ? removeDiscountDay(discDayProp, discDayDate, discDayEnd) : applyDiscountDay()} disabled={discDayBusy}
              className="bg-amber-400 hover:bg-amber-500 text-white font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors disabled:opacity-50 flex items-center gap-2">
              {discDayBusy ? <Spinner size={14} className="animate-spin" /> : <Tag size={14} />}
              {Number(discDayExact) > 0 ? "Set this price" : discDayPct === 0 ? "Back to normal rate" : "Apply discount"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stay-extension offer Dialog (#6) */}
      <Dialog open={!!extendBooking} onOpenChange={(o) => !o && setExtendBooking(null)}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-md shadow-xl">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white">Offer a discounted extension</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              {extendBooking && (<>For <strong>{extendBooking.guest_name || "this guest"}</strong> at {propMap[extendBooking.property_id] || "your property"}. Creates an off-platform direct booking for the extra nights, starting {extendBooking.check_out}.</>)}
            </DialogDescription>
          </DialogHeader>

          {!extResult ? (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Discount off your nightly rate</label>
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">−{extPct}%</span>
                </div>
                <input type="range" min={0} max={50} step={1} value={extPct}
                  onChange={e => setExtPct(Number(e.target.value))}
                  className="w-full accent-black dark:accent-white cursor-pointer mt-1" />
                <p className="text-[11px] text-slate-400 mt-1">
                  The guest opens the link, picks how many nights to extend, and pays online. It becomes a direct booking (no OTA commission).
                </p>
              </div>
              <DialogFooter>
                <button onClick={() => setExtendBooking(null)} className={btnCancel}>Cancel</button>
                <button onClick={createExtension} disabled={extBusy}
                  className="bg-amber-400 hover:bg-amber-500 text-white font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors disabled:opacity-50 flex items-center gap-2">
                  {extBusy ? <Spinner size={14} className="animate-spin" /> : <CalendarPlus size={14} />}
                  Create offer link
                </button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30 rounded-xl p-3 text-sm space-y-1">
                <div className="flex justify-between text-slate-600 dark:text-slate-300">
                  <span>{extResult.discount_pct}% off · from {extResult.check_in}</span>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">₹{extResult.nightly_rate?.toLocaleString("en-IN")}/night</span>
                </div>
                <div className="text-[11px] text-slate-400">Guest can extend up to {extResult.max_nights} night(s).</div>
              </div>
              <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-700 rounded-xl px-3 py-2">
                <span className="flex-1 min-w-0 truncate text-xs font-mono text-slate-600 dark:text-slate-300">{extResult.offer_url}</span>
                <button onClick={() => { navigator.clipboard.writeText(extResult.offer_url); toast.success("Link copied"); }}
                  className="shrink-0 text-xs text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-1"><Copy size={12} />Copy</button>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Add a personal note (optional)</label>
                <textarea rows={2} value={extNote} onChange={e => setExtNote(e.target.value)}
                  placeholder="e.g. It's been lovely having you — we'd be happy to host you a bit longer!"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-300" />
                <p className="text-[10px] text-slate-400 mt-0.5">Links aren't allowed in the note (the offer link is already included). It's sent as a normal message from you.</p>
              </div>
              <button onClick={sendExtensionWa} disabled={extSending || extSent}
                className="w-full py-2.5 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-xl transition disabled:opacity-60 flex items-center justify-center gap-1.5">
                {extSending ? <Spinner size={14} className="animate-spin" /> : <WhatsappLogo size={14} weight="fill" />}
                {extSent ? "Sent to guest ✓" : "Send offer on WhatsApp"}
              </button>
              <div className="flex items-center gap-2">
                <a href={`https://wa.me/${(extResult.guest_phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(extResult.wa_text || extResult.offer_url)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex-1 text-center py-2 text-xs text-slate-500 hover:text-green-600 font-medium transition">
                  or send from my WhatsApp
                </a>
                <button onClick={() => setExtendBooking(null)} className="flex-1 py-2 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 font-medium transition">Done</button>
              </div>
              <p className="text-[11px] text-slate-400">The guest picks their nights and pays online. It lands on your calendar as a confirmed direct booking. Offer valid 48 hours.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-md shadow-xl">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white">Add Guest Review</DialogTitle>
            <DialogDescription className="text-slate-300">Log a guest rating for a property.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Property</Label>
              <Select value={reviewForm.property_id} onValueChange={v => setReviewForm(f => ({...f, property_id: v}))}>
                <SelectTrigger className={selectTriggerCls}><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Guest Name</Label>
              <Input value={reviewForm.guest_name} onChange={e => setReviewForm(f => ({...f, guest_name: e.target.value}))} className={inputCls} />
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Rating</Label>
              <div className="flex gap-2 mt-1">
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => setReviewForm(f => ({...f, rating: s}))} className="p-1">
                    <Star size={24} weight={s <= reviewForm.rating ? "fill" : "regular"} className={s <= reviewForm.rating ? "text-amber-400" : "text-slate-300"} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-200 text-xs font-semibold">Review Text</Label>
              <Input value={reviewForm.review_text} onChange={e => setReviewForm(f => ({...f, review_text: e.target.value}))} className={inputCls} placeholder="Great guest, left the place clean..." />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setReviewDialogOpen(false)} className={btnCancel}>Cancel</button>
            <button onClick={handleSaveReview} className={btnPrimary} data-testid="save-review-button">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function defaultBookingForm() {
  return {
    property_id: "", guest_name: "", guest_phone: "", guest_state: "", check_in: "", check_out: "",
    amount: "", amount_paid: "", platform: "Direct Booking", status: "confirmed", supply_type: "",
    charge_gst: false, custom_price: false, guest_id_url: "",
    notes: "", guest_ids: [],
    early_checkin_requested: false, late_checkout_requested: false,
  };
}
